/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
/*
Author: Gilang Romadhanu Tartila
*/

import React from 'react';
import { useDispatch } from 'react-redux';
import { TouchableOpacity } from 'react-native';
import { getDocumentAsync } from 'expo-document-picker';
import Toast from 'react-native-toast-message';

import Colors from '@global/colors';
import { isWeb, isWebPlatform } from '@global/screens';
import { getAttibutes, csvToArray, uploadS3 } from '@global/helpers';

import SociollaText from '@components/sociolla/SociollaText';

import { showPopup } from '@/store/modal';
import useApi from '@/hooks/useApi';
import { MsAccount } from '@api/ms-account';
import { MsCatalog } from '@api/ms-catalog';

const ButtonUploadCSV = ({
  type = 'customer',
  style = {},
  onCallback = () => {},
}) => {
  const dispatch = useDispatch();
  const getCustomers = useApi(MsAccount.getCustomers);
  const getProducts = useApi(MsCatalog.getProducts);

  const isChangeStyle = () => {
    return !isWeb && (type == 'customer' || type == 'product');
  };

  const onUpload = async () => {
    if (isWebPlatform) {
      const uploadFile = await getDocumentAsync({
        type: 'text/csv',
        copyToCacheDirectory: false,
      });
      if (!uploadFile.canceled) {
        dispatch(showPopup({ name: 'isLoading', value: true }));
        try {
          // eslint-disable-next-line no-undef
          const reader = new FileReader();
          reader.onload = async () => {
            const result = csvToArray(reader.result);
            if (result?.length) {
              if (type == 'customer') {
                const ids = result.filter((a) => a._id).map((a) => a._id);
                if (ids?.length) {
                  const s3Url = await uploadS3({
                    file: uploadFile.assets[0] || {},
                    isNeedMobileSupport: true,
                  });
                  if (!s3Url) {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: 'Failed to upload file. Please try again.',
                      type: 'error',
                    });
                    return;
                  }
                  const response = await getCustomers.request({
                    params: {
                      fields: '_id name',
                      limit: ids.length,
                      filter: {
                        csv: {
                          url: s3Url,
                          field: '_id',
                        },
                      },
                    },
                  });
                  if (response?.data?.data?.length) {
                    const datas = response.data.data;
                    onCallback(
                      datas.map((item) => {
                        item.id = item._id;
                        return item;
                      }),
                    );
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({
                      text1: `${datas.length} data found from csv file.`,
                      type: 'success',
                    });
                  } else {
                    dispatch(showPopup({ name: 'isLoading', value: false }));
                    Toast.show({ text1: 'CSV data not found.', type: 'error' });
                  }
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({ text1: 'CSV data not found.', type: 'error' });
                }
              } else {
                const references = result
                  .filter((a) => a.reference)
                  .map((a) => a.reference.replace(/\r/, ''));
                if (references?.length) {
                  const totalData = references.length;
                  const dataLimit = 50;
                  const maxLoop =
                    totalData >= dataLimit
                      ? Math.ceil(totalData / dataLimit)
                      : 1;
                  let dataSkip = 0;
                  const finalData = [];
                  for (let i = 0; i < maxLoop; i++) {
                    const new_limit = dataLimit * (i + 1);
                    const activeReference = references.slice(
                      dataSkip,
                      new_limit,
                    );
                    dataSkip += dataLimit;
                    const response = await getProducts.request({
                      params: {
                        limit: dataLimit,
                        skip: 0,
                        filter: {
                          'combinations.reference': { $in: activeReference },
                        },
                      },
                    });
                    if (response?.data?.data?.length) {
                      const datas = response.data.data;
                      for (let j = 0; j < datas.length; j++) {
                        const pr = datas[j];
                        if (pr?.combinations?.length) {
                          for (let k = 0; k < pr.combinations.length; k++) {
                            const com = pr.combinations[k];
                            const foundProduct = activeReference.find(
                              (ref) => ref === com.reference,
                            );
                            if (foundProduct) {
                              const combination_id = com._id;
                              const sku = com.reference;
                              const getImage = com.images.find(
                                (a) => a.is_cover || a.url,
                              );
                              const image = getImage?.url;
                              finalData.push({
                                id: pr.id,
                                name: pr.name,
                                brand: {
                                  id: pr.brand?.id,
                                  name: pr.brand?.name,
                                },
                                combination_id: combination_id,
                                variant: getAttibutes(com.attributes),
                                sku: sku,
                                image: image,
                                quantity: 1,
                              });
                            }
                          }
                        }
                      }
                    }
                  }
                  onCallback(finalData);
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({
                    text1: `${finalData.length} data found from csv file.`,
                    type: 'success',
                  });
                } else {
                  dispatch(showPopup({ name: 'isLoading', value: false }));
                  Toast.show({ text1: 'CSV data not found.', type: 'error' });
                }
              }
            } else {
              dispatch(showPopup({ name: 'isLoading', value: false }));
              Toast.show({ text1: 'CSV data not found.', type: 'error' });
            }
          };
          reader.readAsBinaryString(uploadFile.output[0]);
        } catch (err) {
          console.log('Something wrong!', err);
          dispatch(showPopup({ name: 'isLoading', value: false }));
          Toast.show({ text1: 'A system error occurred', type: 'error' });
        }
      }
    } else {
      console.log('Mobile apps not supported.');
    }
  };
  return (
    <TouchableOpacity
      activeOpacity={0.6}
      style={{
        width: isChangeStyle() ? '95%' : 150,
        height: 36,
        borderRadius: 10,
        borderWidth: isChangeStyle() ? 1 : 0,
        borderColor: isChangeStyle() ? Colors.blue1 : '',
        backgroundColor: isChangeStyle() ? Colors.white : Colors.blue3,
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingHorizontal: 10,
        marginLeft: 15,
        marginTop: isChangeStyle() ? 10 : 0,
        marginBottom: 15,
        ...style,
      }}
      onPress={() => onUpload()}>
      <SociollaText
        type="medium"
        style={{
          fontSize: 14,
          marginRight: 10,
          color: isChangeStyle() ? Colors.blue1 : Colors.white,
        }}>
        Upload CSV
      </SociollaText>
    </TouchableOpacity>
  );
};

export default ButtonUploadCSV;
